import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { AzimuthalMap } from '../components/AzimuthalMap';
import { FlightList } from '../components/FlightList';
import { FlightDetails } from '../components/FlightDetails';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { Crown, MapPin, Globe } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Country bounding boxes for filtering flights
const COUNTRY_BOUNDS = {
    "Afghanistan": { minLat: 29.4, maxLat: 38.5, minLon: 60.5, maxLon: 74.9 },
    "Albania": { minLat: 39.6, maxLat: 42.7, minLon: 19.3, maxLon: 21.1 },
    "Algeria": { minLat: 19.0, maxLat: 37.1, minLon: -8.7, maxLon: 12.0 },
    "Argentina": { minLat: -55.1, maxLat: -21.8, minLon: -73.6, maxLon: -53.6 },
    "Australia": { minLat: -43.6, maxLat: -10.7, minLon: 113.2, maxLon: 153.6 },
    "Austria": { minLat: 46.4, maxLat: 49.0, minLon: 9.5, maxLon: 17.2 },
    "Bangladesh": { minLat: 20.7, maxLat: 26.6, minLon: 88.0, maxLon: 92.7 },
    "Belgium": { minLat: 49.5, maxLat: 51.5, minLon: 2.5, maxLon: 6.4 },
    "Brazil": { minLat: -33.8, maxLat: 5.3, minLon: -73.9, maxLon: -34.8 },
    "Canada": { minLat: 41.7, maxLat: 83.1, minLon: -141.0, maxLon: -52.6 },
    "Chile": { minLat: -55.9, maxLat: -17.5, minLon: -75.6, maxLon: -66.9 },
    "China": { minLat: 18.2, maxLat: 53.6, minLon: 73.5, maxLon: 134.8 },
    "Colombia": { minLat: -4.2, maxLat: 12.5, minLon: -79.0, maxLon: -66.9 },
    "Czech Republic": { minLat: 48.6, maxLat: 51.1, minLon: 12.1, maxLon: 18.9 },
    "Denmark": { minLat: 54.6, maxLat: 57.8, minLon: 8.1, maxLon: 15.2 },
    "Egypt": { minLat: 22.0, maxLat: 31.7, minLon: 25.0, maxLon: 36.9 },
    "Finland": { minLat: 59.8, maxLat: 70.1, minLon: 20.6, maxLon: 31.6 },
    "France": { minLat: 41.4, maxLat: 51.1, minLon: -5.1, maxLon: 9.6 },
    "Germany": { minLat: 47.3, maxLat: 55.1, minLon: 5.9, maxLon: 15.0 },
    "Greece": { minLat: 34.8, maxLat: 41.8, minLon: 19.4, maxLon: 29.6 },
    "Hong Kong": { minLat: 22.2, maxLat: 22.6, minLon: 113.8, maxLon: 114.4 },
    "Hungary": { minLat: 45.7, maxLat: 48.6, minLon: 16.1, maxLon: 22.9 },
    "Iceland": { minLat: 63.4, maxLat: 66.5, minLon: -24.5, maxLon: -13.5 },
    "India": { minLat: 6.8, maxLat: 35.5, minLon: 68.2, maxLon: 97.4 },
    "Indonesia": { minLat: -11.0, maxLat: 6.1, minLon: 95.0, maxLon: 141.0 },
    "Iran": { minLat: 25.1, maxLat: 39.8, minLon: 44.0, maxLon: 63.3 },
    "Iraq": { minLat: 29.1, maxLat: 37.4, minLon: 38.8, maxLon: 48.6 },
    "Ireland": { minLat: 51.4, maxLat: 55.4, minLon: -10.5, maxLon: -6.0 },
    "Israel": { minLat: 29.5, maxLat: 33.3, minLon: 34.3, maxLon: 35.9 },
    "Italy": { minLat: 36.6, maxLat: 47.1, minLon: 6.6, maxLon: 18.5 },
    "Japan": { minLat: 24.2, maxLat: 45.5, minLon: 123.0, maxLon: 146.0 },
    "Jordan": { minLat: 29.2, maxLat: 33.4, minLon: 34.9, maxLon: 39.3 },
    "Kenya": { minLat: -4.7, maxLat: 4.6, minLon: 33.9, maxLon: 41.9 },
    "Kuwait": { minLat: 28.5, maxLat: 30.1, minLon: 46.6, maxLon: 48.4 },
    "Lebanon": { minLat: 33.1, maxLat: 34.7, minLon: 35.1, maxLon: 36.6 },
    "Libya": { minLat: 19.5, maxLat: 33.2, minLon: 9.3, maxLon: 25.2 },
    "Malaysia": { minLat: 0.9, maxLat: 7.4, minLon: 99.6, maxLon: 119.3 },
    "Mexico": { minLat: 14.5, maxLat: 32.7, minLon: -117.1, maxLon: -86.7 },
    "Morocco": { minLat: 27.7, maxLat: 35.9, minLon: -13.2, maxLon: -1.0 },
    "Nepal": { minLat: 26.4, maxLat: 30.5, minLon: 80.1, maxLon: 88.2 },
    "Netherlands": { minLat: 50.8, maxLat: 53.5, minLon: 3.4, maxLon: 7.2 },
    "New Zealand": { minLat: -47.3, maxLat: -34.4, minLon: 166.4, maxLon: 178.6 },
    "Nigeria": { minLat: 4.3, maxLat: 13.9, minLon: 2.7, maxLon: 14.7 },
    "Norway": { minLat: 58.0, maxLat: 71.2, minLon: 4.6, maxLon: 31.1 },
    "Oman": { minLat: 16.7, maxLat: 26.4, minLon: 52.0, maxLon: 59.8 },
    "Pakistan": { minLat: 23.7, maxLat: 37.1, minLon: 60.9, maxLon: 77.8 },
    "Peru": { minLat: -18.3, maxLat: -0.1, minLon: -81.3, maxLon: -68.7 },
    "Philippines": { minLat: 4.6, maxLat: 21.1, minLon: 116.9, maxLon: 126.6 },
    "Poland": { minLat: 49.0, maxLat: 54.8, minLon: 14.1, maxLon: 24.1 },
    "Portugal": { minLat: 36.9, maxLat: 42.2, minLon: -9.5, maxLon: -6.2 },
    "Qatar": { minLat: 24.5, maxLat: 26.2, minLon: 50.8, maxLon: 51.6 },
    "Romania": { minLat: 43.6, maxLat: 48.3, minLon: 20.3, maxLon: 29.7 },
    "Russia": { minLat: 41.2, maxLat: 81.9, minLon: 19.6, maxLon: 180.0 },
    "Saudi Arabia": { minLat: 16.4, maxLat: 32.2, minLon: 34.6, maxLon: 55.7 },
    "Singapore": { minLat: 1.2, maxLat: 1.5, minLon: 103.6, maxLon: 104.0 },
    "South Africa": { minLat: -34.8, maxLat: -22.1, minLon: 16.5, maxLon: 32.9 },
    "South Korea": { minLat: 33.2, maxLat: 38.6, minLon: 124.6, maxLon: 130.9 },
    "Spain": { minLat: 36.0, maxLat: 43.8, minLon: -9.3, maxLon: 4.3 },
    "Sri Lanka": { minLat: 5.9, maxLat: 9.8, minLon: 79.7, maxLon: 81.9 },
    "Sweden": { minLat: 55.3, maxLat: 69.1, minLon: 11.1, maxLon: 24.2 },
    "Switzerland": { minLat: 45.8, maxLat: 47.8, minLon: 6.0, maxLon: 10.5 },
    "Taiwan": { minLat: 21.9, maxLat: 25.3, minLon: 120.0, maxLon: 122.0 },
    "Thailand": { minLat: 5.6, maxLat: 20.5, minLon: 97.3, maxLon: 105.6 },
    "Turkey": { minLat: 35.8, maxLat: 42.1, minLon: 26.0, maxLon: 44.8 },
    "Ukraine": { minLat: 44.4, maxLat: 52.4, minLon: 22.1, maxLon: 40.2 },
    "United Arab Emirates": { minLat: 22.6, maxLat: 26.1, minLon: 51.6, maxLon: 56.4 },
    "United Kingdom": { minLat: 49.9, maxLat: 60.8, minLon: -8.2, maxLon: 1.8 },
    "United States of America": { minLat: 24.5, maxLat: 49.4, minLon: -124.8, maxLon: -66.9 },
    "Venezuela": { minLat: 0.6, maxLat: 12.2, minLon: -73.4, maxLon: -59.8 },
    "Vietnam": { minLat: 8.6, maxLat: 23.4, minLon: 102.1, maxLon: 109.5 },
};

export const Dashboard = () => {
    const { isAuthenticated, isPremium, selectedCountry, subscriptionType, token } = useAuth();
    const navigate = useNavigate();
    
    const [flights, setFlights] = useState([]);
    const [selectedFlight, setSelectedFlight] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [mapSelectedCountry, setMapSelectedCountry] = useState(""); // Country selected on map
    const [searchFilter, setSearchFilter] = useState(""); // Search filter for flight list and map
    const [flightTrack, setFlightTrack] = useState(null); // Track data for selected flight (premium)

    // Fetch flight track when a flight is selected (premium only)
    useEffect(() => {
        const fetchFlightTrack = async () => {
            if (!isPremium || !selectedFlight || !token) {
                setFlightTrack(null);
                return;
            }
            
            try {
                const response = await axios.get(
                    `${API}/flights/${selectedFlight.icao24}/track`,
                    {
                        params: { minutes: 60 },
                        headers: { Authorization: `Bearer ${token}` }
                    }
                );
                
                if (response.data && response.data.track) {
                    setFlightTrack(response.data.track);
                } else {
                    setFlightTrack([]);
                }
            } catch (err) {
                console.log('Track not available yet:', err.message);
                setFlightTrack([]);
            }
        };
        
        fetchFlightTrack();
        
        // Refresh track data periodically when following a flight
        const interval = setInterval(fetchFlightTrack, 30000); // Every 30s
        return () => clearInterval(interval);
    }, [selectedFlight, isPremium, token]);

    // Determine which country to filter by
    // Priority: Map selection > User's saved preference (for free users)
    const filterCountry = useMemo(() => {
        // If map has a country selected, use that
        if (mapSelectedCountry) {
            return mapSelectedCountry;
        }
        // Public (not logged in) or Premium users see all flights by default
        if (!isAuthenticated || isPremium) {
            return null;
        }
        // Free users see only their selected country
        return selectedCountry;
    }, [isAuthenticated, isPremium, selectedCountry, mapSelectedCountry]);

    // Filter flights based on user's subscription
    const filteredFlights = useMemo(() => {
        if (!filterCountry) {
            return flights; // Show all flights for public/premium
        }
        
        // Filter flights to user's selected country
        const bounds = COUNTRY_BOUNDS[filterCountry];
        if (!bounds) {
            return flights;
        }
        
        return flights.filter(flight => {
            if (!flight.latitude || !flight.longitude) return false;
            return (
                flight.latitude >= bounds.minLat &&
                flight.latitude <= bounds.maxLat &&
                flight.longitude >= bounds.minLon &&
                flight.longitude <= bounds.maxLon
            );
        });
    }, [flights, filterCountry]);

    const fetchFlights = useCallback(async () => {
        try {
            setIsLoading(true);
            setError(null);
            
            // Build request params
            const params = {
                limit: 2000,
                premium: isPremium  // Request premium data if user is premium
            };
            
            // If free user has selected country, pass it for real-only enhanced data
            if (filterCountry && !isPremium) {
                const bounds = COUNTRY_BOUNDS[filterCountry];
                if (bounds) {
                    params.min_lat = bounds.minLat;
                    params.max_lat = bounds.maxLat;
                    params.min_lon = bounds.minLon;
                    params.max_lon = bounds.maxLon;
                    params.country = filterCountry; // Triggers real-only mode with enhanced info
                }
            } else {
                // World view
                params.min_lat = -90;
                params.max_lat = 90;
                params.min_lon = -180;
                params.max_lon = 180;
            }
            
            const response = await axios.get(`${API}/flights`, { params });
            
            setFlights(response.data.flights);
            
            // Log if using enhanced data
            if (response.data.is_premium_data) {
                console.log('Using enhanced flight data with route information');
            }
        } catch (err) {
            console.error('Error fetching flights:', err);
            setError('Failed to load flight data');
            toast.error('Failed to load flight data');
        } finally {
            setIsLoading(false);
        }
    }, [isPremium, filterCountry]);

    useEffect(() => {
        fetchFlights();
        
        // Auto-refresh every 30 seconds
        const interval = setInterval(fetchFlights, 30000);
        
        return () => clearInterval(interval);
    }, [fetchFlights]);

    const handleFlightSelect = useCallback((flight) => {
        setSelectedFlight(flight);
    }, []);

    const handleCloseDetails = useCallback(() => {
        setSelectedFlight(null);
    }, []);

    // Check if user is free tier with selected country
    const showCountryBadge = isAuthenticated && !isPremium && selectedCountry;

    return (
        <div className="min-h-screen bg-[#071D35] pt-16">
            {/* Country Filter Badge for Free Users */}
            {showCountryBadge && (
                <div className="bg-[#f07050]/10 border-b border-[#f07050]/30">
                    <div className="max-w-7xl mx-auto px-4 py-2 flex items-center justify-center gap-3">
                        <MapPin className="h-4 w-4 text-[#f07050]" />
                        <span className="text-sm text-white">
                            Showing flights in <span className="font-bold text-[#f07050]">{selectedCountry}</span>
                        </span>
                        <span className="text-sm text-[#94a3b8]">|</span>
                        <Button
                            size="sm"
                            variant="link"
                            onClick={() => navigate('/profile')}
                            className="text-[#f07050] hover:text-[#e06040] p-0 h-auto"
                            data-testid="upgrade-for-worldwide-btn"
                        >
                            <Globe className="h-3 w-3 mr-1" />
                            Upgrade for worldwide access
                        </Button>
                    </div>
                </div>
            )}

            {/* Main Dashboard */}
            <div className="max-w-[1920px] mx-auto p-4 md:p-6">
                {error ? (
                    <div className="flex flex-col items-center justify-center min-h-[600px]">
                        <div className="glass-card rounded-xl p-8 text-center max-w-md">
                            <h2 className="text-2xl font-bold text-white mb-4">
                                {error}
                            </h2>
                            <p className="text-[#94a3b8] mb-6">
                                Please try refreshing the page.
                            </p>
                            <Button
                                onClick={fetchFlights}
                                className="bg-[#f07050] hover:bg-[#e06040] text-white btn-primary-glow"
                            >
                                Retry
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
                        {/* Map Area */}
                        <div className="lg:col-span-8 xl:col-span-9 h-[600px]">
                            <AzimuthalMap
                                flights={filteredFlights}
                                selectedFlight={selectedFlight}
                                onFlightSelect={handleFlightSelect}
                                isLoading={isLoading}
                                onRefresh={fetchFlights}
                                userCountry={filterCountry}
                                onCountrySelect={setMapSelectedCountry}
                                searchFilter={searchFilter}
                                flightTrack={flightTrack}
                                isPremium={isPremium}
                            />
                        </div>

                        {/* Sidebar */}
                        <div className="lg:col-span-4 xl:col-span-3 h-[600px] flex flex-col gap-4">
                            {/* Flight Details (if selected) */}
                            {selectedFlight ? (
                                <FlightDetails
                                    flight={selectedFlight}
                                    onClose={handleCloseDetails}
                                />
                            ) : null}

                            {/* Flight List */}
                            <div className={selectedFlight ? 'flex-1 min-h-0' : 'h-full'}>
                                <FlightList
                                    flights={filteredFlights}
                                    selectedFlight={selectedFlight}
                                    onFlightSelect={handleFlightSelect}
                                    isLoading={isLoading}
                                    isPremiumData={isPremium}
                                    searchFilter={searchFilter}
                                    onSearchChange={setSearchFilter}
                                />
                            </div>
                        </div>
                    </div>
                )}

                {/* Stats Bar */}
                <div className="mt-6 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="glass-card rounded-lg p-4">
                        <p className="data-label mb-1">
                            {filterCountry ? `Flights in ${filterCountry}` : 'Total Flights'}
                        </p>
                        <p className="text-2xl font-bold text-white">{filteredFlights.length}</p>
                    </div>
                    <div className="glass-card rounded-lg p-4">
                        <p className="data-label mb-1">In Air</p>
                        <p className="text-2xl font-bold text-green-500">
                            {filteredFlights.filter(f => !f.on_ground).length}
                        </p>
                    </div>
                    <div className="glass-card rounded-lg p-4">
                        <p className="data-label mb-1">Countries</p>
                        <p className="text-2xl font-bold text-[#f07050]">
                            {new Set(filteredFlights.map(f => f.origin_country).filter(Boolean)).size}
                        </p>
                    </div>
                    <div className="glass-card rounded-lg p-4">
                        <p className="data-label mb-1">Avg. Altitude</p>
                        <p className="text-2xl font-bold text-white">
                            {filteredFlights.length > 0 
                                ? Math.round(filteredFlights.reduce((sum, f) => sum + (f.altitude_feet || 0), 0) / filteredFlights.length).toLocaleString()
                                : '---'
                            }
                            <span className="text-sm text-[#64748b] ml-1">ft</span>
                        </p>
                    </div>
                </div>
            </div>
        </div>
    );
};
